import sumBy from 'lodash/sumBy';
import autobind from 'autobind';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import compact from 'lodash/compact';
import flatMap from 'lodash/flatMap';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import { transliterate } from 'transliteration';

import { EhrApi } from '@api/EhrApi';
import { ehrQueryOptions } from '@patient/queries/ehrQueries';
import { queryClient } from '@store/queryClient';
import { EntryApi } from '@api/EntryApi';
import { InformedConsentApi } from '@api/InformedConsentApi';
import { PersonalAccountApi } from '@api/PersonalAccountApi';
import { ReferralApi } from '@api/ReferralApi';
import { RenderedServiceApi } from '@api/RenderedServiceApi';
import { ReservationApi } from '@api/ReservationApi';

import {
    Channel,
    ContactPersonData,
    DiagnosisResponseParams,
    EhrBlockParams,
    EhrData,
    EhrTranslationData,
    EntryTypeFieldName,
    InformedConsentStatus,
    Nullable,
    PersonalAccountItem,
    ReferralData,
    ReservationStatus,
    Sex,
} from 'chaika-frontend-types/frontend-cms';
import { EhrViewContext, Locale, SavedPaymentMethodStatus, ServiceCategory } from 'chaika-enums';

import { store } from '@store';
import { CommonRelation, commonService } from '@store/common/commonService';
import { dictionaryService } from '@template/services/dictionary/dictionaryService';
import { calculateAvailableReservationDateInterval } from '@patient/store/entry/calculateAvailableReservationDateInterval';
import { EntryFields } from '@store/models/EntryFields';
import { patientService } from '@patient/services/patient/patientService';
import { serviceService } from '@services/services/serviceService';
import { userService } from '@user/services/userService';

import { DateHelper } from '@utils/DateHelper';

import { Ehr } from '@patient/store/ehr/Ehr';
import { loadOfficesByIds } from '@services/queries/officeQueries';
import { collectReservationExecutorIds } from './reservationExecutorAndOrganization';
import { entryListService, LoadEntryListOptions } from './entryListService';
import { representativeService } from './representativeService';
import { subscriptionService } from '../subscriptionService';
import { medicalCaseService } from './medicalCaseService';

import { AnyObject, SYSTEM_USER_ID } from '@store/types';
import { translate } from '@chaika/i18n/helper';
import { isDefined } from '@utils/isDefined';
import { assertIsDefined } from '@utils/assertIsDefined';
import { SubscriptionOrderApi } from '@api/SubscriptionOrderApi';
import { RecommendationApi } from '@api/RecommendationApi';
import { SavedPaymentMethodApi } from '@api';
import isEmpty from 'lodash/isEmpty';

const updateApi = AwesomeDebouncePromise(EhrApi.update, 500);

export enum EhrRelation {
    Entry = 'entry',
    Field = 'field',
    EntriesHistory = 'entriesHistory',
    Subscriptions = 'subscriptions',
    Cards = 'Cards',
    Representatives = 'Representatives',
    MedicalCases = 'MedicalCases',
    ActiveReservations = 'activeReservations',
    AssistantsChatLink = 'assistantsChatLink',
}

const transliterationEhrFields = new Map([
    ['firstName', 'engFirstName'],
    ['lastName', 'engLastName'],
    ['patronymic', 'engPatronymic'],
    ['identityDocIssuer', 'identityDocIssuerTransliteration'],
    ['address', 'addressTransliteration'],
    ['registrationAddress', 'registrationAddressTransliteration'],
    ['internationalIdAuthority', 'internationalIdAuthorityTransliteration'],
]);

class EhrService {
    private relationLoader = {
        [EhrRelation.Entry]: this.loadEntryList,
        [EhrRelation.Field]: this.loadFieldData,
        [EhrRelation.EntriesHistory]: this.loadEntriesHistory,
        [EhrRelation.AssistantsChatLink]: this.loadAssistantsChatLink,
        [EhrRelation.Subscriptions]: subscriptionService.load,
        [EhrRelation.Cards]: this.loadCards,
        [EhrRelation.ActiveReservations]: this.loadActivePatientReservations,
        [EhrRelation.Representatives]: representativeService.load,
        // Иногда возникает проблема из за круговой зависимости, внутри использует ehrService
        [EhrRelation.MedicalCases]: () => medicalCaseService.loadWithVersions(),
    };

    public async loadBasic(id: string | number, viewContext?: EhrViewContext): Promise<EhrData> {
        const ehrParams = await queryClient.ensureQueryData(ehrQueryOptions.detail(String(id), viewContext));

        Object.assign(store.ehr, new Ehr(ehrParams));

        return ehrParams;
    }

    public async load(id: string | number, relations?: EhrRelation[], viewContext?: EhrViewContext): Promise<void> {
        const ehrParams = await this.loadBasic(id, viewContext);
        const ehrFiles = await EhrApi.getFiles(ehrParams.id);

        store.ehr.setFiles(ehrFiles);
        store.profileForm.setEhr(ehrParams);

        if (relations) {
            await this.loadRelations(relations);
        }
    }

    public async loadRecommendations() {
        const recommendations = await RecommendationApi.findByEhrId(store.ehr.id);

        store.ehr.recommendations.set(recommendations);
    }

    public async deleteNotExistsEntryFromElasticCommand(ehrId: string): Promise<void> {
        await EhrApi.deleteNotExistsEntryFromElasticCommand(ehrId);
    }

    public hasSimilarEhrs(): boolean {
        const {
            ehr: { id: originId },
            patientStore,
        } = store;

        const similar = patientStore.getSimilarEhrs(originId).filter(({ id }) => id !== originId);

        return similar.length > 0;
    }

    public async loadRelations(relations: EhrRelation[], options: LoadEntryListOptions = {}): Promise<void> {
        await Promise.all(
            relations.map(relation => {
                if (relation === EhrRelation.Entry) {
                    return this.loadEntryList(options);
                }

                return this.relationLoader[relation]();
            }),
        );
    }

    public async loadEntriesHistory(names?: string[]): Promise<void> {
        const ehr = store.ehr;

        const [historyData] = await Promise.all([
            EhrApi.getEhrFieldHistory(ehr.id, names),
            commonService.load([CommonRelation.FieldsParams]),
        ]);

        if (names && names.includes(EntryTypeFieldName.MedicalCases)) {
            const diagnosis: DiagnosisResponseParams[][] = [];

            await this.loadIcdDictionary(flatten(diagnosis));
        }

        ehr.fieldHistory.set(historyData.reverse());
    }

    public async loadAssistantsChatLink(): Promise<void> {
        const { ehr, authUser } = store;

        if (authUser.isAssistant && !!ehr.id && !ehr.isAssistantsChatsSet) {
            const assistantChats = await SubscriptionOrderApi.getAssistantsChat({
                patientId: ehr.id,
                assistantId: authUser.id,
            });

            store.ehr.setAssistantsChats(assistantChats);
        }
    }

    public async sendRegistrationUrl(locale?: Locale): Promise<void> {
        try {
            await EhrApi.sendRegistrationUrl(store.ehr.id, locale);
        } catch (e: any) {
            store.app.showingPopupParams = {
                title: translate('errorOnSendRegistratioUrl'),
                description: e?.message ?? e,
            };
        }
    }

    public async sendConfirmationCode(locale?: Locale): Promise<void> {
        try {
            await EhrApi.sendConfirmationCode(store.ehr.id, locale);
        } catch (e: any) {
            store.app.showingPopupParams = {
                title: translate('errorOnVerificationCode'),
                description: e?.message ?? e,
            };
        }
    }

    public async loadPersonalAccountItems(): Promise<PersonalAccountItem[]> {
        const items = await PersonalAccountApi.getItems(store.ehr.id);
        store.ehr.personalAccountItems.set(items);
        store.ehr.personalAccountBalance = sumBy(items, 'balance');
        return items;
    }

    public async loadReservations(): Promise<void> {
        const ehr = store.ehr;

        const { reservations } = await EntryApi.findReservations({
            patientId: ehr.id,
            ...calculateAvailableReservationDateInterval(),
        });

        await serviceService.loadByIds(flatMap(reservations, 'serviceIds'));
        ehr.availableReservationList.set(reservations ?? []);
    }

    public async loadPatientReservations(): Promise<void> {
        const { id: patientId, reservationList } = store.ehr;

        const reservations = await ReservationApi.find({
            patientId,
            status: [ReservationStatus.Reserved, ReservationStatus.Cancelled, ReservationStatus.VisitOccurred].join(
                ',',
            ),
            withAssociatedServiceIds: true,
        });
        reservationList.set(reservations);

        await Promise.all([this.loadPatientReservationsWithUsers(), this.loadPatientReservationServices()]);
    }

    @autobind
    public async loadActivePatientReservations(): Promise<void> {
        const { id: patientId, activeReservations } = store.ehr;

        const reservations = await ReservationApi.find({
            patientId,
            status: ReservationStatus.Reserved,
            withAssociatedServiceIds: true,
        });
        activeReservations.set(reservations);

        await Promise.all([this.loadPatientReservationsWithUsers(), this.loadPatientReservationServices()]);
    }

    public async loadPatientReservationServices(): Promise<void> {
        const { reservationList, activeReservations } = store.ehr;

        const serviceIds = flatten(
            [...reservationList.values, ...activeReservations.values].map(
                ({ associatedServiceIds }) => associatedServiceIds,
            ),
        ).map(serviceId => ({ serviceId }));
        await this.loadServices(serviceIds);
    }

    public async loadPatientReservationsWithUsers(): Promise<void> {
        const { reservationList, activeReservations } = store.ehr;

        await this.loadPatientReservationOffices();

        store.ehr.patientReservations.add(reservationList.values);
        store.ehr.activePatientReservations = activeReservations.values;

        await this.loadPatientReservationUsers();
    }

    public async loadPatientReservationUsers() {
        const { reservationList, activeReservations, patientReservations } = store.ehr;

        const ids = compact(
            [...reservationList.values, ...activeReservations.values, ...patientReservations.values]
                .map(({ authorId }) => authorId)
                .filter(id => !!id && id !== SYSTEM_USER_ID),
        );
        const executorIds = flatMap(
            [...reservationList.values, ...activeReservations.values],
            collectReservationExecutorIds,
        );

        await userService.loadIfNotExist(uniq([...ids, ...executorIds]));
    }

    public async loadInformedConsentForReservation(reservationId: string, serviceId: string): Promise<void> {
        const { ehr } = store;
        if (!ehr.hasInformedConsent(reservationId, serviceId)) {
            const informedConsents = await InformedConsentApi.getList({ reservationId, ehrId: ehr.id });
            const informedConsent =
                informedConsents.length > 0
                    ? (informedConsents.find(({ status }) => status === InformedConsentStatus.Signed) ??
                      informedConsents[0])
                    : null;
            ehr.setInformedConsent(reservationId, serviceId, informedConsent);
        }
    }

    public async updateSex(value: string) {
        store.app.doOrShowError({
            action: async () => {
                await EhrApi.update(store.ehr.id, { sex: value as Sex });
                store.ehr.sex = value as Sex;
            },
            errorMessage: translate('errorOnEditEntry'),
        });
    }

    public async updatePrimaryChannel(primaryChannel: Nullable<Channel>) {
        store.app.doOrShowError({
            action: async () => {
                await EhrApi.update(store.ehr.id, { primaryChannel });

                store.ehr.setPrimaryChannel(primaryChannel);
            },
            errorMessage: translate('errorOnPrimaryChannelChange'),
        });
    }

    public async updateTranslations(translations: EhrTranslationData[]): Promise<void> {
        await EhrApi.update(store.ehr.id, { translations });
    }

    public async updateFieldValue(name: string, value: string | boolean) {
        const currentValue = (store.ehr as AnyObject)[name];
        store.profileForm.setFieldValue(name, value);

        const newValue = store.profileForm.getFormattedFieldValue(name);

        if (store.profileForm.isFieldValid(name)) {
            if (transliterationEhrFields.has(name)) {
                await this.updateTransliterationField(name, newValue as string);
            } else {
                try {
                    await EhrApi.update(store.ehr.id, { [name]: newValue });
                    (store.ehr as AnyObject)[name] = newValue;
                } catch (e: any) {
                    console.error(e);
                    store.app.showingPopupParams = {
                        title: translate('errorOnSaveData'),
                        description: e.name === 'Conflict' ? translate('ehrHasDuplicateCantChange') : e.message,
                    };
                    store.profileForm.setFieldValue(name, currentValue);
                    (store.ehr as AnyObject)[name] = currentValue;
                }
            }
        }
    }

    public async addContactPerson(contactPerson: ContactPersonData) {
        store.app.doOrShowError({
            action: async () => {
                const contactPersons = [...store.profileForm.serializeContactPersons(), contactPerson];
                await EhrApi.update(store.ehr.id, { contactPersons });

                store.profileForm.addContactPersons(contactPerson);
                store.ehr.addContactPerson(contactPerson);
            },
            errorMessage: translate('errorOnEditEntry'),
        });
    }

    public async updateContactPersons() {
        store.app.doOrShowError({
            action: async () => {
                const contactPersons = store.profileForm.serializeContactPersons();

                await updateApi(store.ehr.id, { contactPersons });

                store.ehr.setContactPersons(contactPersons);
            },
            errorMessage: translate('errorOnEditEntry'),
        });
    }

    public async updatePaymentComment(paymentComment: string): Promise<void> {
        store.app.doOrShowError({
            action: async () => {
                await EhrApi.update(store.ehr.id, { paymentComment });

                store.ehr.setPaymentComment(paymentComment);
            },
            errorMessage: translate('errorOnEditEntry'),
        });
    }

    public reset() {
        store.profileForm.reset();
    }

    public async block(params: EhrBlockParams) {
        store.app.doOrShowError({
            action: async () => {
                await EhrApi.block(store.ehr.id, params);
                store.ehr.block(params);
            },
            errorMessage: translate('errorOnBlockEhr'),
        });
    }

    public async unblock() {
        store.app.doOrShowError({
            action: async () => {
                await EhrApi.unblock(store.ehr.id);
                store.ehr.unblock();
            },
            errorMessage: translate('errorOnUnblockEhr'),
        });
    }

    public async loadTodayRenderedServices(): Promise<void> {
        const ehr = store.ehr;
        const today = new Date();
        if (isDefined(ehr.id)) {
            const { renderedServices } = await RenderedServiceApi.getList({
                ehrId: ehr.id,
                createTimeFrom: DateHelper.getStartOfDay(today).toISOString(),
                createTimeTo: DateHelper.getEndOfDay(today).toISOString(),
            });

            store.ehr.todayRenderedServices.set(renderedServices);
        }
    }

    public async reloadReferrals(): Promise<void> {
        const ehr = store.ehr;

        const referrals = await ReferralApi.getList({ ehrId: ehr.id });

        await this.loadReferralData(referrals);

        ehr.fields.set(EntryTypeFieldName.Referrals, referrals);
    }

    public async loadVaccinationReferrals(): Promise<void> {
        const ehr = store.ehr;
        ehr.vaccinationReferralList.reset();

        const referrals = await ReferralApi.getList({ ehrId: ehr.id, includeCategory: ServiceCategory.Vaccination });

        await this.loadReferralData(referrals);

        ehr.vaccinationReferralList.set(referrals);
    }

    public async loadEntriesForList(entryIds: string[]) {
        const { id, entryList } = store.ehr;

        await entryListService.addToEntryList(entryList, { ehrIds: id, ids: entryIds.join() });
    }

    @autobind
    protected async loadFieldData() {
        const ehr = store.ehr;

        const [ehrFieldData, referrals] = await Promise.all([
            EhrApi.getEhrFieldDate(ehr.id),
            ReferralApi.getList({ ehrId: ehr.id }),
            commonService.load([CommonRelation.FieldsParams]),
        ]);
        const fieldNames = Object.keys(ehrFieldData);

        const fieldData = fieldNames.map(name => ({
            name,
            value: (ehrFieldData as AnyObject)[name],
        }));

        await Promise.all([
            this.loadIcdDictionary((ehrFieldData as AnyObject)[EntryTypeFieldName.MedicalCases]),
            this.loadReferralData(referrals),
        ]);

        ehr.fields = new EntryFields([
            ...fieldData,
            {
                name: 'referrals',
                value: referrals,
            },
        ]);
    }

    @autobind
    protected async loadEntryList(options: LoadEntryListOptions = {}) {
        const { id, entryList } = store.ehr;
        await entryListService.loadEntryList(entryList, { ehrIds: id }, options);
    }

    protected async loadCards() {
        const ehr = store.ehr;

        const savedPaymentMethods = await SavedPaymentMethodApi.find({
            userId: ehr.id,
            status: SavedPaymentMethodStatus.Active,
        });

        if (!isEmpty(savedPaymentMethods)) {
            await patientService.loadByIds(savedPaymentMethods.map(method => method.ownerId));
        }

        ehr.cards = savedPaymentMethods;
    }

    private async updateTransliterationField(filedName: string, newValue: string): Promise<void> {
        const engFieldName = transliterationEhrFields.get(filedName);
        assertIsDefined(engFieldName, `Field ${filedName} is updated only when it has a transliterated pair`);
        const newEngValue = transliterate(newValue);
        const currentValue = (store.ehr as AnyObject)[filedName];
        const currentEngValue = (store.ehr as AnyObject)[engFieldName];
        try {
            await EhrApi.update(store.ehr.id, {
                [filedName]: newValue,
                [engFieldName]: newEngValue,
            });
            (store.ehr as AnyObject)[filedName] = newValue;
            (store.ehr as AnyObject)[engFieldName] = newEngValue;
            store.profileForm.setFieldValue(engFieldName, newEngValue);
        } catch (e: any) {
            console.error(e);
            store.app.showingPopupParams = {
                title: translate('errorOnSaveData'),
                description: e.name === 'Conflict' ? translate('ehrHasDuplicateCantChange') : e.message,
            };
            (store.ehr as AnyObject)[filedName] = currentValue;
            (store.ehr as AnyObject)[engFieldName] = currentEngValue;
            store.profileForm.setFieldValue(filedName, currentValue);
            store.profileForm.setFieldValue(engFieldName, currentEngValue);
        }
    }

    private async loadIcdDictionary(diagnosis: DiagnosisResponseParams[]) {
        const icdIds: string[] = [];

        (diagnosis || []).forEach(mediscalCase => {
            icdIds.push((mediscalCase as AnyObject).icdId);
        });

        const uniqIds = uniq(icdIds);

        if (uniqIds && uniqIds.length > 0) {
            await dictionaryService.loadDictionaryItemsByIds(uniqIds);
        }
    }

    private async loadReferralData(referrals: ReferralData[]): Promise<void> {
        const userIds = compact(
            flatten(referrals.map(referral => [(referral as AnyObject).userId, (referral as AnyObject).authorId])),
        );
        await Promise.all([this.loadServices(referrals), userService.loadIfNotExist(userIds)]);
    }

    private async loadServices(params: { serviceId: string }[]) {
        const servicesIds = params.map(param => (param as AnyObject).serviceId);
        await serviceService.loadByIds(servicesIds);
    }

    private async loadPatientReservationOffices(): Promise<void> {
        const { reservationList, activeReservations } = store.ehr;

        const officeIds = uniq(
            compact([...reservationList.values, ...activeReservations.values].map(({ officeId }) => officeId)),
        );

        if (officeIds.length === 0) {
            return;
        }

        store.officeList.add(await loadOfficesByIds(officeIds));
    }
}

export const ehrService = new EhrService();
